import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { noteChatActivity, resetChatActivity } from '@/components/chat/chatActivityStore';
import { chatKey, targetForKind } from '@/components/chat/chatTarget';
import {
  formatChatMessagePreview,
  type ChatMessagePreviewFields,
} from '@/components/chat/formatChatMessagePreview';
import type { ChatTarget } from '@/components/chat/types';
import { supabase } from '@/lib/supabase';
import { getActiveChatKey } from '@/utils/activeChatKey';
import { ChatUnreadContext, type ChatUnreadValue } from '@/hooks/useChatUnread';
import { logger } from '@/utils/logger';

/** A burst of read receipts (opening a chat writes one per message) collapses into one refetch. */
const REFETCH_DEBOUNCE_MS = 400;

type UnreadRow = { chat_kind: ChatTarget['kind']; chat_id: string; unread_count: number };

/** Which thread a messages_new row belongs to, as a chatKey(). */
function keyForMessageRow(row: Record<string, unknown>): string | null {
  const pairs: [ChatTarget['kind'], string][] = [
    ['event', 'event_id'],
    ['department', 'department_chat_id'],
    ['direct', 'direct_chat_id'],
  ];
  for (const [kind, column] of pairs) {
    const id = row[column];
    if (typeof id === 'string' && id) return chatKey(targetForKind(kind, id));
  }
  return null;
}

/**
 * Unread counts for every conversation this viewer can see.
 *
 * A context because two unrelated places need the same numbers: the Communication list
 * and the nav badge, which is on screen everywhere.
 */
export function ChatUnreadProvider({
  userId,
  children,
}: {
  userId: string | undefined;
  children: ReactNode;
}) {
  const [countByKey, setCountByKey] = useState<Map<string, number>>(new Map());
  const debounceRef = useRef<number | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!userId) {
      setCountByKey(new Map());
      return;
    }
    const { data, error } = await supabase.rpc('get_chat_unread_counts');
    if (error) {
      logger.error('ChatUnreadProvider fetch failed', error);
      return;
    }
    const next = new Map<string, number>();
    for (const row of (data ?? []) as UnreadRow[]) {
      if (!row.chat_id) continue;
      next.set(chatKey(targetForKind(row.chat_kind, row.chat_id)), row.unread_count);
    }
    setCountByKey(next);
  }, [userId]);

  const refetch = useCallback(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      void fetchCounts();
    }, REFETCH_DEBOUNCE_MS);
  }, [fetchCounts]);

  useEffect(() => {
    void fetchCounts();
  }, [fetchCounts]);

  // Previews are keyed by thread, not by viewer, so a sign-out or an account switch has
  // to empty them -- the next account may well be able to see the same threads.
  useEffect(() => {
    resetChatActivity();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`chat-unread:${userId}`)
      // Realtime applies RLS per subscriber, so only threads this viewer can read arrive.
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages_new' },
        ({ new: row }) => {
          const message = row as Record<string, unknown>;
          const key = keyForMessageRow(message);
          if (!key) return;

          // Publish the message to the chat-activity store before any of the unread
          // filtering below. The Communication list has to show every message in a
          // thread's preview -- including this viewer's own, sent from another device,
          // and including the thread they have open -- where the unread count must
          // ignore exactly those two. One subscription, two consumers with different
          // rules; see chatActivityStore.ts for why this is not on the context value.
          noteChatActivity(key, {
            lastMessageAt: message['created_at'] as string,
            preview: formatChatMessagePreview(message as ChatMessagePreviewFields),
          });

          if (message['user_id'] === userId) return;
          // The thread on screen is being read as it arrives; its receipt is about to
          // land and trigger a refetch of its own.
          if (key === getActiveChatKey()) return;

          // Refetch rather than increment locally. The RPC hides threads the app does
          // not list -- ended events, departments dropped from events.departments -- and
          // a local +1 knows nothing about that, so it would re-inflate the badge with
          // conversations there is no way to open. Debounced, so a burst is one call.
          refetch();
        }
      )
      // Opening a chat writes one receipt per message, so this is a debounced refetch
      // rather than an attempt to decrement precisely.
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_reads',
          filter: `user_id=eq.${userId}`,
        },
        () => refetch()
      )
      .subscribe();

    // Receipts written on another device, or messages missed while backgrounded, only
    // show up on a full refetch.
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [userId, refetch]);

  const value = useMemo<ChatUnreadValue>(() => {
    let total = 0;
    for (const count of countByKey.values()) total += count;
    return { countByKey, total, refetch };
  }, [countByKey, refetch]);

  return <ChatUnreadContext.Provider value={value}>{children}</ChatUnreadContext.Provider>;
}
